/** ApexCharts option factories for Result Comparison Insights */

/** Myskool light theme series colours */
const SERIES_COLORS = [
  "#7367f0",
  "#28c76f",
  "#ff9f43",
  "#00cfe8",
  "#ea5455",
  "#9e95f5",
];

export function buildGroupedBarOptions(categories = []) {
  return {
    chart: {
      type: "bar",
      toolbar: { show: false },
      fontFamily: "Outfit, Inter, sans-serif",
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 700,
        animateGradually: { enabled: true, delay: 90 },
        dynamicAnimation: { enabled: true, speed: 350 },
      },
    },
    colors: SERIES_COLORS,
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "52%",
        borderRadius: 5,
        borderRadiusApplication: "end",
        dataLabels: { position: "top" },
      },
    },
    dataLabels: { enabled: false },
    stroke: { show: true, width: 2, colors: ["transparent"] },
    xaxis: {
      categories,
      labels: {
        style: { colors: "#64748b", fontSize: "11px", fontWeight: 600 },
        rotate: categories.length > 8 ? -35 : 0,
        hideOverlappingLabels: true,
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      title: {
        text: "Average marks",
        style: { color: "#94a3b8", fontWeight: 600, fontSize: "11px" },
      },
      labels: {
        style: { colors: "#94a3b8", fontSize: "11px" },
        formatter: (v) => (v == null ? "" : Number(v).toFixed(0)),
      },
      max: 100,
      min: 0,
    },
    fill: { opacity: 1 },
    // Custom HTML legend is rendered outside the chart for long campus names
    legend: { show: false },
    grid: {
      borderColor: "#eef2f7",
      strokeDashArray: 3,
      padding: { top: 8, right: 8, bottom: 0, left: 8 },
    },
    tooltip: {
      theme: "light",
      y: {
        formatter: (val) =>
          val == null || Number.isNaN(Number(val))
            ? "—"
            : `${Number(val).toFixed(1)} avg`,
      },
    },
  };
}

export function buildPassRankBarOptions(labels = []) {
  return {
    chart: {
      type: "bar",
      toolbar: { show: false },
      fontFamily: "Outfit, Inter, sans-serif",
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 650,
        animateGradually: { enabled: true, delay: 80 },
      },
    },
    colors: SERIES_COLORS,
    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 6,
        barHeight: "58%",
        distributed: true,
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (v) => `${Number(v).toFixed(0)}%`,
      style: { fontSize: "11px", fontWeight: 700, colors: ["#fff"] },
      offsetX: 0,
    },
    xaxis: {
      categories: labels,
      max: 100,
      labels: {
        style: { colors: "#94a3b8", fontSize: "11px" },
        formatter: (v) => `${v}%`,
      },
    },
    yaxis: {
      labels: {
        style: { colors: "#334155", fontSize: "11px", fontWeight: 700 },
        maxWidth: 120,
        formatter: (val) => {
          const s = String(val == null ? "" : val);
          if (s.length <= 18) return s;
          return `${s.slice(0, 16)}…`;
        },
      },
    },
    legend: { show: false },
    grid: {
      borderColor: "#eef2f7",
      xaxis: { lines: { show: true } },
      yaxis: { lines: { show: false } },
      padding: { left: 4, right: 12 },
    },
    tooltip: {
      y: { formatter: (val) => `${Number(val).toFixed(1)}% pass` },
      x: {
        formatter: (_val, opts) => {
          const cats = opts?.w?.globals?.labels || [];
          const i = opts?.dataPointIndex;
          return cats[i] != null ? String(cats[i]) : "";
        },
      },
    },
  };
}

export function buildRadarOptions(categories = []) {
  return {
    chart: {
      type: "radar",
      toolbar: { show: false },
      fontFamily: "Outfit, Inter, sans-serif",
    },
    colors: SERIES_COLORS.slice(0, 2),
    stroke: { width: 2 },
    fill: { opacity: 0.2 },
    markers: { size: 3 },
    xaxis: {
      categories,
      labels: {
        style: {
          colors: Array(categories.length).fill("#64748b"),
          fontSize: "10px",
        },
      },
    },
    yaxis: { show: false, max: 100, min: 0 },
    legend: {
      position: "bottom",
      fontWeight: 600,
    },
    plotOptions: {
      radar: {
        polygons: {
          strokeColors: "#e2e8f0",
          connectorColors: "#e2e8f0",
          fill: { colors: ["#fff", "#f8fafc"] },
        },
      },
    },
  };
}

/**
 * Transform API/mock payload into Apex series for grouped bar (average by subject).
 */
export function toGroupedBarSeries(data) {
  const dimensions = data.dimensions || [];
  const subjects = data.subjects || [];
  return dimensions.map((dim) => ({
    name: dim,
    data: subjects.map((subj) => {
      const row = (subj.series || []).find(
        (s) => s.key === dim || s.label === dim
      );
      return row ? Number(row.average) || 0 : 0;
    }),
  }));
}

export function toPassRankSeries(data) {
  const dimensions = data.dimensions || [];
  const subjects = data.subjects || [];
  const values = dimensions.map((dim) => {
    let total = 0;
    let count = 0;
    subjects.forEach((subj) => {
      const row = (subj.series || []).find(
        (s) => s.key === dim || s.label === dim
      );
      if (row && row.passPercent != null) {
        total += Number(row.passPercent);
        count += 1;
      }
    });
    return count ? total / count : 0;
  });
  return [{ name: "Pass %", data: values }];
}

export function toRadarSeries(data, focusKey) {
  const subjects = data.subjects || [];
  const dimensions = data.dimensions || [];
  const focus = focusKey || dimensions[0];
  const peerAvgs = subjects.map((subj) => {
    const vals = (subj.series || [])
      .map((s) => Number(s.average))
      .filter((n) => !Number.isNaN(n));
    if (!vals.length) return 0;
    return vals.reduce((a, b) => a + b, 0) / vals.length;
  });
  const focusVals = subjects.map((subj) => {
    const row = (subj.series || []).find(
      (s) => s.key === focus || s.label === focus
    );
    return row ? Number(row.average) || 0 : 0;
  });
  return [
    { name: focus || "Selected", data: focusVals },
    { name: "Peer average", data: peerAvgs },
  ];
}

export { SERIES_COLORS };
